import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "综艺", to: "/category/variety" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setKeyword("");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-300/60"
          : "bg-gradient-to-b from-background-50/90 to-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20 gap-4">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-play-fill text-xl" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading font-extrabold text-lg md:text-xl text-foreground-950 tracking-tight">
                亚洲视频一区
              </span>
              <span className="hidden sm:block text-[10px] tracking-[0.2em] text-foreground-600 font-label">
                ASIA VIDEO ONE
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "text-primary-500"
                      : "text-foreground-700 hover:text-foreground-950 hover:bg-background-200/70"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Desktop search */}
          <form
            onSubmit={submitSearch}
            className="hidden md:flex items-center gap-2 bg-background-200/80 border border-background-300/60 rounded-full pl-4 pr-1.5 py-1.5 w-56 lg:w-72 focus-within:border-primary-500/70 transition-colors"
          >
            <i className="ri-search-line text-foreground-500 text-base" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="search"
              placeholder="搜索影片、剧集、综艺"
              className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 outline-none"
            />
            <button
              type="submit"
              aria-label="搜索"
              className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-line text-base" />
            </button>
          </form>

          {/* Mobile toggle */}
          <button
            type="button"
            aria-label="菜单"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg border border-background-300/60 text-foreground-900 cursor-pointer"
          >
            <i className={open ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} />
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      {open && (
        <div className="lg:hidden bg-background-50/98 backdrop-blur-md border-t border-background-300/60 animate-fade-in">
          <div className="px-4 py-4 flex flex-col gap-3">
            <form
              onSubmit={submitSearch}
              className="flex items-center gap-2 bg-background-200/80 border border-background-300/60 rounded-full pl-4 pr-1.5 py-1.5"
            >
              <i className="ri-search-line text-foreground-500 text-base" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="search"
                placeholder="搜索影视内容"
                className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 outline-none"
              />
              <button
                type="submit"
                aria-label="搜索"
                className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 cursor-pointer"
              >
                <i className="ri-arrow-right-line text-base" />
              </button>
            </form>
            <div className="grid grid-cols-2 gap-2">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-lg text-sm font-medium text-center whitespace-nowrap transition-colors ${
                      isActive
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-200/70 text-foreground-800"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}